import { getStripeDate } from "../../../../utils/format-stripe-date";
import { formatNumber } from "../../../../utils/format-sub";

/**
 * §5.10: "Failed payouts must raise a count on both products' Attention
 * tabs." A payout failing on a closed bank account means money stops
 * arriving while every Income screen still looks healthy — the worst
 * failure mode in the feature.
 *
 * Renders nothing at zero, so it never becomes furniture, and sits above
 * per-member payment failures because money not reaching the bank at all
 * outranks one member's card declining.
 */
const FailedPayoutsCallout = ({ failedPayouts, currency = "usd", t }) => {
  const { count = 0, amount = 0, payouts = [] } = failedPayouts || {};
  if (!count) return null;

  // `amount` is a sum of payout rows, which are in settlement currency (what
  // Stripe actually pays out in), not the `currency` prop, which is
  // transaction currency (what customers were charged in) — the two differ
  // on real accounts. Each payout carries its own correct currency; use the
  // first failed payout's, falling back to the prop only if `payouts` is
  // somehow empty (defensive — count > 0 should always mean payouts is
  // non-empty).
  const settlementCurrency = payouts[0]?.currency || currency;

  return (
    <section className="failed-payouts-callout" data-severity="high">
      <header>
        <h3>{t("income.failedPayouts.title", { count })}</h3>
        <p>
          {t("income.failedPayouts.blurb", {
            amount: formatNumber(amount, settlementCurrency),
          })}
        </p>
      </header>
      <ul>
        {payouts.map((payout) => {
          const { day, month, year } = getStripeDate(payout.at);
          return (
            <li key={payout.id}>
              <span>
                {formatNumber(
                  Math.abs(payout.amount),
                  payout.currency || currency
                )}
              </span>
              <span>{`${month} ${day}, ${year}`}</span>
              {/* Plain-language reason; the raw Stripe code only when it's
                  one we don't have words for (sc-46067). */}
              <span>
                {payout.failureCode
                  ? t(`income.failedPayouts.reasons.${payout.failureCode}`, {
                      defaultValue: payout.failureCode.replace(/_/g, " "),
                    })
                  : t("income.failedPayouts.noReason")}
              </span>
            </li>
          );
        })}
      </ul>
      <a
        href="/coaching-portal/income?context=all&tab=payouts"
        data-button-type="secondary"
      >
        {t("income.payouts.seeAll")}
      </a>
    </section>
  );
};

export default FailedPayoutsCallout;
